/**
 * Client-side validation based on jquery.validation plugin
 *
 * https://github.com/Home24/jquery-validation
 * Documentation: http://jqueryvalidation.org/documentation/
 *
 * The difference between original library and fork by home24:
 *      - multiple error types
 *          rule example:
 *              "email": { - field name
 *                  "warning.pattern": { - rule name
 *                      "rule": "/^[a-z0-9_-]+([.|+][a-z0-9_-]+)*[@][a-z0-9-]+([.][a-z0-9-]+)*[.]([a-z]{2,}|xn--[a-z0-9]+)$/i", - rule
 *                      "validationType": "warning", - rule type
 *                      "validationMethod": "pattern" - validation method
 *                  }
 *              }
 *              or just:
 *              "email": { - field name
 *                  "pattern" : "/^[a-z0-9_-]+([.|+][a-z0-9_-]+)*[@][a-z0-9-]+([.][a-z0-9-]+)*[.]([a-z]{2,}|xn--[a-z0-9]+)$/i" - validation method: rule
 *              }
 *              by default rules have error type.
 *          message example:
 *              "email": {
 *                  "error.maxLength": "Der Eintrag ist zu lang, bitte geben Sie höchstens {0} Zeichen ein."
 *              }
 *              or:
 *              "email": {
 *                  "maxLength": "Der Eintrag ist zu lang, bitte geben Sie höchstens {0} Zeichen ein."
 *              }
 *      - extend errorClass for custom error type.
 *          By default it is string like:
 *              errorClass: 'error'
 *          Using with multiple error types:
 *              errorClass: {
 *                  error: 'error' - class for default error type
 *                  warning: 'error--new-warning-class'
 *              }
 *
 *      - disabled email validation use pattern rule instead
 *      - changed remote method
 *          expect answer from server like
 *              {"valid": true} or {"valid": false}
 *              it is possible to send error custom message with server answer
 *              {
 *                  "valid": false,
 *                  "message": "error message"
 *              }
 *
 */

import $ from 'jquery';
import _ from 'underscore';
import Backbone from 'backbone';
import 'home24-jquery-validation';

import { get as t } from '../../home24/core/translation';
import validateIBAN from '../../home24-shared/validation/validateIBAN';

export default Backbone.View.extend({
  selectors: {
    inputFields: 'input, textarea, select',
    ignore:
      '[type="submit"], [type="hidden"]:not([data-validate="true"]), [data-validate="false"], .warning',
    inputGroup: '.ui-input-group',
    inputGroupSet: '.ui-input-group__set',
    inputLabel: '.input-label, label:not(.ca-label)',
    inputCustomLabel: '.ca-label',
    inputFilledIcon: '.icon-filled-field',
    inputInvalidIcon: '.icon-invalid-field',
    valid: '.valid',
    invalidate: '.is-invalidate',
    errorClass: 'is-invalidate error-message',
    warningClass: 'warning-message',
    errorLabelClass: 'error-label',
    hiddenClass: 'is-hidden'
  },

  /**
   * Filter for available rules on client side. Prevent applying non-existing rule to validator
   */
  availableRules: {
    error: [
      'minLength',
      'maxLength',
      'pattern',
      'blacklist',
      'blacklistPattern',
      'required',
      'email',
      'matchesInput',
      'remote'
    ],
    warning: [
      'minLength',
      'maxLength',
      'pattern',
      'blacklist',
      'blacklistPattern',
      'required',
      'email',
      'matchesInput'
    ]
  },

  translation: {},

  validationRules: {},

  formValidatorId: null,

  fieldNames: [],

  initialize: function({ validationRules }) {
    $.validator.addMethod('iban', validateIBAN, t('ui.input.validate.iban'));

    this.validationRules = validationRules;
    this.translation = $.extend(true, {}, t('ui.input.validation'));
    this.removeOldValidator();
    this.formValidatorId = this.checkFormValidatorId(this.$el);
    this.fieldNames = this.getFieldNames(this.$el);

    var defaultOnkeyup = $.validator.defaults.onkeyup;

    $.validator.setDefaults({
      onkeyup(element, event) {
        if ($(element).data('validation-keyup') === false) {
          return;
        }

        defaultOnkeyup.apply(this, arguments);
      }
    });

    var rules = this.generateRules(
      this.fieldNames,
      $.extend(true, {}, validationRules),
      this.availableRules,
      this.formValidatorId
    );

    var messages = this.generateMessages(rules);

    $.extend($.validator.messages, this.getDefaultMessages());
    this.validator = this.$el.validate({
      onfocusout: this.onFocusOut,
      onchange: this.onFocusOut,
      errorClass: {
        error: this.selectors.errorClass,
        warning: this.selectors.warningClass
      },
      errorElement: 'div',
      rules: rules,
      messages: messages,
      submitHandler: $.proxy(this.submitHandler, this),
      errorPlacement: $.proxy(this.errorPlacement, this),
      success: $.proxy(this.successHandler, this),
      ignore: this.selectors.ignore
    });
  },

  successHandler: function($error, element) {
    var $element = $(element);
    var $inputGroup = $error.closest(this.selectors.inputGroup);
    var $customLabel = $inputGroup.find(this.selectors.inputCustomLabel);
    var $label = $inputGroup.find(this.selectors.inputLabel);
    var $inputGroupSet = $inputGroup.find(this.selectors.inputGroupSet);

    $error.remove();

    if ($customLabel.length) {
      $label = $label.add($customLabel);
    }

    if (
      !(
        $inputGroupSet.length &&
        $inputGroupSet.find(this.selectors.invalidate).not($element).length
      )
    ) {
      $label.removeClass(this.selectors.errorLabelClass);
    }

    // if (!($inputGroupSet.length && $inputGroupSet.find(this.selectors.valid).not($element).length === 0) && $element.val().length) {
    //     $element.siblings(this.selectors.inputFilledIcon).removeClass(this.selectors.hiddenClass);
    // }
    if ($element.val().length) {
      $element
        .siblings(this.selectors.inputFilledIcon)
        .removeClass(this.selectors.hiddenClass);

      $element
        .siblings(this.selectors.inputInvalidIcon)
        .addClass(this.selectors.hiddenClass);
    }
  },

  errorPlacement: function($error, $element) {
    var $inputGroup = $element.closest(this.selectors.inputGroup);
    var $label = $inputGroup.find(this.selectors.inputLabel).eq(0);
    var $customLabel = $inputGroup.find(this.selectors.inputCustomLabel);

    if ($customLabel.length && !$label.length) {
      $inputGroup.prepend($error);
      $customLabel.addClass(this.selectors.errorLabelClass);
    } else {
      if ($label.length) {
        $label.addClass(this.selectors.errorLabelClass).after($error);
      } else {
        $inputGroup.prepend($error);
      }
    }

    $element
      .siblings(this.selectors.inputFilledIcon)
      .addClass(this.selectors.hiddenClass);

    $element
      .siblings(this.selectors.inputInvalidIcon)
      .removeClass(this.selectors.hiddenClass);
  },

  onFocusOut: function(element) {
    $(element).valid();
  },

  /**
   * Get form validator id
   * @param {$} $form
   * @returns {string|Null}
   */
  checkFormValidatorId: function($form) {
    return $form.data('formvalidatorid') || null;
  },

  /**
   * Get form field names
   * @param {$} $form
   * @returns {Array}
   */
  getFieldNames: function($form) {
    var fieldNames = [];
    var $fields = $form
      .find(this.selectors.inputFields)
      .not(this.selectors.ignore);

    _.each($fields, function(field) {
      fieldNames.push(field.name);
    });

    return fieldNames;
  },

  /**
   * Generate rules from config for jQueryValidation Plugin
   *
   * @param {Array} fieldNames
   * @param {Object} validationRules
   * @param {Object} availableRules
   * @param {string} formValidatorId
   * @returns {Object}
   *
   * Example
   * {
   *  "email": {
   *      "error.maxLength": {
   *          "rule": "255",
   *          "validationType": "error",
   *          "validationMethod": "maxLength"
   *      },
   *      "warning.pattern": {
   *          "rule": "/^[a-z0-9_-]+([.|+][a-z0-9_-]+)*[@][a-z0-9-]+([.][a-z0-9-]+)*[.]([a-z]{2,}|xn--[a-z0-9]+)$/i",
   *          "validationType": "warning",
   *          "validationMethod": "pattern"
   *      },
   *      "error.required": {
   *          "rule": true,
   *          "validationType": "error",
   *          "validationMethod": "required"
   *      }
   *  }
   * }
   */
  generateRules: function(
    fieldNames,
    validationRules,
    availableRules,
    formValidatorId
  ) {
    var self = this;
    var generatedRules = {};

    _.each(fieldNames, function(fieldName) {
      var fieldRules = validationRules[fieldName];

      if (fieldRules) {
        var generatedFieldRules = {};

        if (formValidatorId && fieldRules[formValidatorId]) {
          fieldRules = $.extend(
            true,
            {},
            fieldRules,
            fieldRules[formValidatorId]
          );
        }

        _.each(availableRules, function(rules, errorType) {
          if (errorType === 'error') {
            $.extend(
              generatedFieldRules,
              self.convertRulesNames(
                self.convertRulesFromConfig(fieldRules, rules),
                errorType
              )
            );
          } else {
            if (fieldRules[errorType]) {
              $.extend(
                generatedFieldRules,
                self.convertRulesNames(
                  self.convertRulesFromConfig(fieldRules[errorType], rules),
                  errorType
                )
              );
            }
          }
        });

        generatedRules[fieldName] = generatedFieldRules;
      }
    });

    return generatedRules;
  },

  /**
   * Convert rules to jQueryValidation format
   *
   * @param {Object} rules
   * @param {Object} errorRules
   * @returns {Object}
   */
  convertRulesFromConfig: function(rules, errorRules) {
    if ('required' in rules) {
      rules.required =
        rules.required.length > 1 ? rules.required : Boolean(rules.required);
    }

    if ('testEmpty' in rules) {
      rules.required = true;
    }

    if (rules.blacklistPattern) {
      rules.blacklistPattern = {
        patterns: rules.blacklistPattern
      };
    }

    if (rules.blacklist) {
      rules.blacklist = {
        patterns: rules.blacklist,
        filterPattern: rules.filterPatternBeforeBlacklist || null
      };
    }

    if (rules.options && rules.options.matchesInput) {
      var id = this.$el.attr('id');

      if (!id) {
        id = this.cid;
        this.$el.prop('id', id);
      }

      rules.matchesInput = '#' + id + ' ' + rules.options.matchesInput;
    }

    return _.pick(rules, errorRules);
  },

  /**
   *  Add errorType prefix to rules
   *
   * @param {Object} rules
   * @param {string} errorType
   * @returns {Object}
   */
  convertRulesNames: function(rules, errorType) {
    var convertedRules = {};

    _.each(rules, function(rule, validationMethod) {
      convertedRules[errorType + '.' + validationMethod] = {
        rule: rule,
        validationType: errorType,
        validationMethod: validationMethod
      };
    });

    return convertedRules;
  },

  getDefaultMessages: function() {
    return {
      required: this.translation.standardMessages.required,
      minLength: this.translation.standardMessages.minLength,
      maxLength: this.translation.standardMessages.maxLength,
      blacklist: this.translation.standardMessages.blacklist,
      blacklistPattern: this.translation.standardMessages.blacklistPattern,
      pattern: this.translation.standardMessages.pattern
    };
  },

  /**
   * Generate translations for jQueryValidation Plugin
   *
   * @param {Object} generatedRules
   * @returns {Object}
   *
   * Example
   *  {
   *      "email": {
   *          "error.maxLength": "Der Eintrag ist zu lang, bitte geben Sie höchstens {0} Zeichen ein.",
   *          "warning.pattern": "Die Email Adresse ist ungültig, bitte korrigieren Sie die Eingabe.",
   *          "error.required": "Dies ist ein Pflichtfeld."
   *      }
   *  }
   */
  generateMessages: function(generatedRules) {
    var generatedMessages = {};
    var self = this;

    _.each(generatedRules, function(generatedRule, fieldName) {
      generatedMessages[fieldName] = {};

      _.each(generatedRule, function(value) {
        generatedMessages[fieldName][
          value.validationType + '.' + value.validationMethod
        ] = self.getMessage(
          value.validationMethod,
          fieldName,
          value.validationType
        );
      });
    });

    return generatedMessages;
  },

  /**
   * Get translated message for specific form, specific field
   *
   * @param {string} propertyName
   * @param {string} fieldName
   * @param {string} errorType
   * @returns {Boolean|Object}
   */
  getMessage: function(propertyName, fieldName, errorType) {
    var result = false;

    if (this.translation[fieldName]) {
      result = this.getFieldSpecificMessage(
        propertyName,
        errorType,
        this.translation[fieldName],
        this.formValidatorId
      );
    }

    if (
      result === false &&
      !_.isUndefined(this.translation.standardMessages[propertyName])
    ) {
      result = this.convertMessage(
        this.translation.standardMessages[propertyName]
      );
    }

    return result;
  },

  /**
   * Get a translated message for a field.
   * @param {string} propertyName
   * @param {string} errorType
   * @param {Object} translationObj
   * @param {string} formValidatorId
   * @returns {Boolean|string}
   */
  getFieldSpecificMessage: function(
    propertyName,
    errorType,
    translationObj,
    formValidatorId
  ) {
    if (
      formValidatorId &&
      translationObj[formValidatorId] &&
      translationObj[formValidatorId][propertyName] &&
      translationObj[formValidatorId][propertyName][errorType]
    ) {
      return this.convertMessage(
        translationObj[formValidatorId][propertyName][errorType]
      );
    }

    if (translationObj[propertyName]) {
      if (translationObj[propertyName][errorType]) {
        return this.convertMessage(translationObj[propertyName][errorType]);
      }

      return false;
    } else if (translationObj.options && translationObj.options[propertyName]) {
      return this.convertMessage(
        translationObj.options[propertyName][errorType]
      );
    }

    return false;
  },

  /**
   * Convert variable in a message for jQueryValidation Plugin
   * @param {string} message
   * @returns {string}
   */
  convertMessage: function(message) {
    var i = 0;

    return message.replace(/%\w*%/g, function() {
      return '{' + i++ + '}';
    });
  },

  removeOldValidator: function() {
    $.removeData(this.el, 'validator');
  },

  /**
   * Return rules
   * @public
   * @param {string|string[]} fieldName
   * @param {string} [formValidatorId]
   * @returns {Object}
   */
  getRules: function(fieldName, formValidatorId) {
    var rules = this.generateRules(
      _.isArray(fieldName) ? fieldName : [fieldName],
      $.extend(true, {}, this.validationRules),
      this.availableRules,
      formValidatorId
    );
    var messages = this.generateMessages(rules);

    _.each(rules, function(value, key) {
      rules[key].messages = messages[key];
    });

    return rules;
  }
});



/** WEBPACK FOOTER **
 ** ./legacy/home24-desktop/validation/validationView.js
 **/